import { useCallback } from 'react';
import { mainApi, yemeksepetiApi, getirApi } from '../../../../config/apiClient';
import { getTodayPackageOrders, updatePackageOrderStatus } from '../../../../services/salesService';
import { convertTrendyolOrderToPackageOrder } from '../utils/helpers';
import { updateStatusInSql } from '../services/TrendyolYemekService';
import { PackageOrder, TrendyolOrder } from '../types';
import { GetirCancelInfo, MigrosStatusUpdateResult } from './useSignalR';
import type { TrendyolUpdateResult } from './useTrendyolYemek';

/**
 * Trendyol 409 self-heal sonrası kullanıcıya gösterilecek insan dostu Türkçe mesajı üretir.
 *
 * @param currentStatus Trendyol'dan tekrar çekilen gerçek `packageStatus` (örn. "Created", "Invoiced", "Cancelled")
 *                      veya `unknown-terminal` / `refresh-failed` özel değerleri.
 * @param attemptedAction Kullanıcının tıkladığı aksiyon ('accept' | 'ready' | 'shipped' | 'delivered').
 */
function buildTrendyolMismatchMessage(
  currentStatus: string,
  attemptedAction: 'accept' | 'ready' | 'shipped' | 'delivered',
): string {
  if (currentStatus === 'refresh-failed') {
    return 'Uber Eats durum çakışması algılandı ancak güncel durum çekilemedi. Lütfen biraz sonra tekrar deneyin.';
  }
  if (currentStatus === 'not-listed' || currentStatus === 'unknown-terminal') {
    // Sipariş Trendyol aktif liste sorgusunda yok → state ve SQL'e dokunmadık.
    // Kullanıcıya yenileme önerisi sun (veriyi bozma riski almıyoruz).
    return 'Bu sipariş Uber Eats\'in aktif sipariş listesinde görünmüyor. Aksiyon Uber Eats tarafından reddedildi. Lütfen "Yenile" butonuna basın veya birkaç saniye sonra tekrar deneyin (yerel veriler korundu).';
  }

  // İptal/teslim gibi terminal durumlar — aksiyondan bağımsız anlamlı mesajlar
  if (currentStatus === 'Cancelled' || currentStatus === 'UnSupplied' || currentStatus === 'Unsupplied') {
    return 'Bu sipariş iptal edilmiş. Liste güncellendi.';
  }
  if (currentStatus === 'Delivered') {
    return 'Bu sipariş zaten Teslim Edildi. Liste güncellendi.';
  }
  if (currentStatus === 'Returned') {
    return 'Bu sipariş iade edilmiş. Liste güncellendi.';
  }

  if (attemptedAction === 'ready') {
    if (currentStatus === 'Created') return 'Bu sipariş henüz onaylanmamış. Önce "Onayla" butonuna basın. Liste güncellendi.';
    if (currentStatus === 'Invoiced') return 'Bu sipariş zaten Hazırlandı olarak işaretlenmiş. Liste güncellendi.';
    if (currentStatus === 'Shipped') return 'Bu sipariş zaten yola çıkmış. Liste güncellendi.';
  }

  if (attemptedAction === 'accept') {
    if (currentStatus === 'Picking' || currentStatus === 'Invoiced' || currentStatus === 'Shipped') {
      return 'Bu sipariş zaten onaylanmış. Liste güncellendi.';
    }
  }

  if (attemptedAction === 'shipped') {
    if (currentStatus === 'Created' || currentStatus === 'Picking') {
      return 'Bu sipariş henüz hazırlanmadı. Önce "Hazır" butonuna basın. Liste güncellendi.';
    }
    if (currentStatus === 'Shipped') {
      return 'Bu sipariş zaten yola çıkmış. Liste güncellendi.';
    }
  }

  if (attemptedAction === 'delivered') {
    if (currentStatus === 'Created' || currentStatus === 'Picking' || currentStatus === 'Invoiced') {
      return 'Bu sipariş henüz teslimat aşamasında değil. Liste güncellendi.';
    }
  }

  return `Bu sipariş Trendyol'da "${currentStatus}" durumunda. Liste güncellendi.`;
}

/**
 * Siparişi id + kaynak ile bulur.
 *
 * Her platformun siparişleri kendi tablosunun identity kolonundan geldiği için
 * iki farklı platformun siparişi aynı id'yi taşıyabiliyor. Kaynak bilinmiyorsa
 * ve id birden fazla kayda denk geliyorsa eşleşme yapılmaz: yanlış platformun
 * API'sine durum güncellemesi göndermektense hiç göndermemek doğrudur.
 */
function findOrderByIdAndSource<T extends { id: number; source?: string }>(
  orders: T[],
  orderId: number,
  source?: string | null,
): T | undefined {
  if (source) {
    const srcLower = String(source).toLowerCase();
    const bySource = orders.find(o => o.id === orderId && String(o.source || '').toLowerCase() === srcLower);
    if (bySource) return bySource;
  }

  const candidates = orders.filter(o => o.id === orderId);
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    console.error('[StatusUpdate] Aynı id birden fazla platformda bulundu, kaynak belirsiz:', {
      orderId,
      beklenenKaynak: source,
      adaylar: candidates.map(c => c.source),
    });
  }
  return undefined;
}

export interface UseOrderStatusHandlersDeps {
  companyId: string | null;
  screenData: any;
  currentUser: { email?: string | null } | null;
  filteredOrders: any[];
  /** Ekranda seçili siparişin kaynağı; id çakışmalarını çözmek için. */
  selectedOrderSource?: string | null;
  getirOrders: any[];
  migrosOrders: any[];
  trendyolOrders: TrendyolOrder[];
  yemeksepetiOrders: any[];
  setSnackbar: (data: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
  loadPackageOrders: () => Promise<void>;
  fetchMasaSiparisleri: () => Promise<void>;
  fetchGetirOrders: () => Promise<void>;
  fetchMigrosOrders: () => Promise<void>;
  fetchYemeksepetiOrders: () => Promise<void>;
  updateMasaSiparisiStatus: (orderId: number, newStatus: string) => Promise<boolean>;
  updateGetirOrderStatus: (orderId: number, newStatus: string) => Promise<{ success: boolean; message?: string }>;
  updateMigrosOrderStatus: (orderId: number, newStatus: string, cancelReasonId?: number) => Promise<MigrosStatusUpdateResult>;
  updateYemeksepetiOrderStatus: (orderId: number, action: string, reason?: string) => Promise<{ success: boolean; message?: string }>;
  updateTrendyolOrderStatus: (packageId: string, action: 'accept' | 'ready' | 'shipped' | 'delivered', preparationTime?: number) => Promise<TrendyolUpdateResult>;
  setGetirCountdownMap: React.Dispatch<React.SetStateAction<Record<string | number, number>>>;
  setSelectCourierModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTrendyolCancelOrder: React.Dispatch<React.SetStateAction<TrendyolOrder | null>>;
  setTrendyolCancelModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTrendyolOrders: React.Dispatch<React.SetStateAction<TrendyolOrder[]>>;
  refreshTrendyolOrders: () => Promise<void>;
  handleOnlinePaymentRecord?: (order: PackageOrder) => Promise<void>;
  onGetirScheduledBlock?: (scheduledDate: string | null, orderId: number) => void;
  onGetirOrderCancelled?: (cancelInfo: GetirCancelInfo) => void;
}

export function useOrderStatusHandlers(deps: UseOrderStatusHandlersDeps) {
  const {
    companyId,
    screenData,
    currentUser,
    filteredOrders,
    selectedOrderSource,
    getirOrders,
    migrosOrders,
    trendyolOrders,
    yemeksepetiOrders,
    setSnackbar,
    loadPackageOrders,
    fetchMasaSiparisleri,
    fetchGetirOrders,
    fetchMigrosOrders,
    fetchYemeksepetiOrders,
    updateMasaSiparisiStatus,
    updateGetirOrderStatus,
    updateMigrosOrderStatus,
    updateYemeksepetiOrderStatus,
    updateTrendyolOrderStatus,
    setGetirCountdownMap,
    setSelectCourierModalOpen,
    setTrendyolCancelOrder,
    setTrendyolCancelModalOpen,
    setTrendyolOrders,
    refreshTrendyolOrders,
    handleOnlinePaymentRecord,
    onGetirScheduledBlock,
    onGetirOrderCancelled,
  } = deps;

  const handleUpdateOrderStatus = useCallback(async (orderId: number, newStatus: string, courierFirebaseUid?: string, courierName?: string) => {
    if (!companyId) {
      setSnackbar({ open: true, message: 'Şirket bilgisi bulunamadı', severity: 'error' });
      return;
    }
    
    // QR/E-Ticaret/Kiosk siparişi mi kontrol et
    const selectedOrder = findOrderByIdAndSource(filteredOrders, orderId, selectedOrderSource);
    const isSignalROrder = selectedOrder && ['qr', 'ecommerce', 'kiosk'].includes(selectedOrder.source);
    
    // QR/E-Ticaret/Kiosk siparişleri için MasaSiparisi API'sini kullan
    if (isSignalROrder) {
      console.log('📦 [StatusUpdate] QR/E-Ticaret/Kiosk siparişi - MasaSiparisi API kullanılıyor...', { orderId, newStatus });
      
      let masaSiparisiStatus = 'Bekliyor';
      switch (newStatus) {
        case 'READY':
          masaSiparisiStatus = 'Hazır';
          break;
        case 'ON_DELIVERY':
          masaSiparisiStatus = 'Yolda';
          break;
        case 'DELIVERED':
          masaSiparisiStatus = 'Teslim Edildi';
          break;
        case 'CANCELLED':
          masaSiparisiStatus = 'İptal';
          break;
        default:
          masaSiparisiStatus = 'Hazırlanıyor';
      }
      
      const success = await updateMasaSiparisiStatus(orderId, masaSiparisiStatus);
      if (success) {
        setSnackbar({ open: true, message: `Sipariş durumu güncellendi: ${masaSiparisiStatus}`, severity: 'success' });
        await fetchMasaSiparisleri();
      } else {
        setSnackbar({ open: true, message: 'Sipariş durumu güncellenemedi', severity: 'error' });
      }
      return;
    }

    // GETİR siparişleri için doğrudan gts_getir_api'yi kullan
    if (selectedOrder?.source === 'getir') {
      console.log('📦 [StatusUpdate] Getir siparişi - Getir API kullanılıyor...', { orderId, newStatus });
      
      const getirOrder = getirOrders.find(o => o.id === orderId);
      if (!getirOrder) {
        setSnackbar({ open: true, message: 'Getir sipariş bilgisi bulunamadı', severity: 'error' });
        return;
      }
      
      const getirAction = newStatus.toLowerCase();
      const statusLabels: Record<string, string> = {
        'verify': 'Onaylandı',
        'prepare': 'Hazırlandı',
        'handover': 'Kuryeye Teslim Edildi',
        'deliver': 'Teslim Edildi',
      };
      const statusLabel = statusLabels[getirAction] || 'Güncellendi';
      
      // İleri tarihli sipariş uyarısı (engelleme yok, sadece bilgilendirme)
      if (getirAction === 'verify' && getirOrder.isScheduled) {
        const scheduledStr = getirOrder.scheduledDate;
        if (scheduledStr) {
          onGetirScheduledBlock?.(scheduledStr, orderId);
          return;
        }
      }

      // Proaktif iptal kontrolü: Getir API'den siparişin güncel durumunu sorgula
      if (getirAction === 'verify' && getirOrder.getirId && process.env.REACT_APP_GETIRAPI_URL) {
        try {
          console.log('🔍 [Getir] Onay öncesi sipariş durumu kontrol ediliyor...', { orderId, getirId: getirOrder.getirId });
          const { data: orderDetail } = await getirApi.get(`/api/getir-orders/order/${orderId}`);
          const orderData = orderDetail?.Data || orderDetail?.data;
          // Getir status: 1500 = müşteri iptali, 1600 = zaman aşımı iptali
          if (orderData && (orderData.Status === 1500 || orderData.Status === 1600 || orderData.status === 1500 || orderData.status === 1600)) {
            console.log('🚫 [Getir] Sipariş zaten iptal edilmiş! Onay engelleniyor.', { status: orderData.Status || orderData.status });
            const cancelReasonText = orderData.CancelReasonText || orderData.cancelReasonText || orderData.CancelNote || orderData.cancelNote || 'Müşteri tarafından iptal edildi';
            const clientName = orderData.ClientName || orderData.clientName || getirOrder.customerName || '';
            onGetirOrderCancelled?.({
              getirId: getirOrder.getirId || String(orderId),
              status: orderData.Status || orderData.status,
              confirmationId: getirOrder.confirmationId || orderData.ConfirmationId || orderData.confirmationId || '',
              cancelReasonText: cancelReasonText,
              clientName: clientName,
              cancelledAt: orderData.CancelledAt || orderData.cancelledAt || new Date().toISOString(),
            });
            await fetchGetirOrders();
            return;
          }
        } catch (checkErr) {
          console.warn('⚠️ [Getir] Onay öncesi durum kontrolü başarısız (devam ediliyor):', checkErr);
        }
      }

      console.log(`🔄 [Getir] Durum güncelleniyor: ${getirOrder.status} → ${getirAction} (DeliveryType=${getirOrder.deliveryType})`);
      const result = await updateGetirOrderStatus(orderId, getirAction);
      
      if (result.success) {
        setSnackbar({ open: true, message: `Getir sipariş durumu güncellendi: ${statusLabel}`, severity: 'success' });
        setGetirCountdownMap(prev => ({ ...prev, [orderId]: 60 }));
        await fetchGetirOrders();

        if (getirAction === 'prepare' && getirOrder.deliveryType === 2) {
          setTimeout(() => { setSelectCourierModalOpen(true); }, 500);
        }

        // Otomatik ödeme kaydı devre dışı - kasiyer manuel olarak "Online Ödeme Al" butonunu kullanacak
        if (getirAction === 'deliver' && selectedOrder.isOnlinePayment && !selectedOrder.isPaid) {
          console.log('[ONLINE_ODEME] Getir teslim edildi - manuel ödeme bekleniyor (oto kayıt devre dışı)');
        }
      } else {
        const errorMsg = result.message || 'Getir sipariş durumu güncellenemedi';
        // Hata mesajı "iptal" içeriyorsa otomatik olarak iptal bildirimi tetikle
        if (errorMsg.toLowerCase().includes('iptal') || errorMsg.toLowerCase().includes('cancel')) {
          console.log('🚫 [Getir] Sipariş iptal edilmiş - iptal bildirimi tetikleniyor');
          onGetirOrderCancelled?.({
            getirId: getirOrder.getirId || String(orderId),
            status: 1500,
            confirmationId: getirOrder.confirmationId || '',
            cancelReasonText: errorMsg,
            clientName: getirOrder.customerName || '',
            cancelledAt: new Date().toISOString(),
          });
          await fetchGetirOrders();
        } else {
          setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        }
      }
      return;
    }

    // MIGROS siparisleri icin gts_migrosYemek_api'yi kullan
    if (selectedOrder?.source === 'migros') {
      console.log('[StatusUpdate] Migros siparisi - Migros API kullaniliyor...', { orderId, newStatus });
      const migrosOrder = migrosOrders.find(o => o.id === orderId);
      if (!migrosOrder) {
        setSnackbar({ open: true, message: 'Migros siparis bilgisi bulunamadi', severity: 'error' });
        return;
      }

      const statusLabels: Record<string, string> = {
        'Approved': 'Onaylandi',
        'Prepared': 'Hazirlandi',
        'Delivery': 'Yola Cikti',
        'Completed': 'Teslim Edildi',
        'Rejected': 'Reddedildi',
      };
      const statusLabel = statusLabels[newStatus] || 'Guncellendi';

      const result = await updateMigrosOrderStatus(orderId, newStatus);
      if (result.success) {
        setSnackbar({ open: true, message: `Migros siparis durumu guncellendi: ${statusLabel}`, severity: 'success' });
        await fetchMigrosOrders();

        if (newStatus === 'Prepared' && migrosOrder.deliveryProvider === 'RESTAURANT') {
          setTimeout(() => { setSelectCourierModalOpen(true); }, 500);
        }

        if (newStatus === 'Completed' && selectedOrder.isOnlinePayment && !selectedOrder.isPaid) {
          console.log('[ONLINE_ODEME] Migros teslim edildi - manuel ödeme bekleniyor (oto kayıt devre dışı)');
        }
      } else if (result.alreadyCancelled) {
        // Migros tarafinda iptal edilmis - backend self-heal etti, sadece UI'i bilgilendir.
        console.warn('[Migros] Siparis Migros tarafinda iptal edilmis (self-heal):', { orderId, migrosStatus: result.migrosStatus });
        setSnackbar({
          open: true,
          message: result.message || 'Bu siparis Migros tarafindan iptal edilmis. Liste guncellendi.',
          severity: 'warning',
        });
        await fetchMigrosOrders();
      } else if (result.inFlight) {
        // Ayni siparis icin istek zaten ucuyor (cift tiklama): sessizce gec.
        console.warn('[Migros] Durum guncelleme zaten suruyor, tekrar istek gonderilmedi:', orderId);
      } else if (result.alreadyInStatus) {
        // Siparis Migros'ta zaten bu durumda; hata degil. Liste tazelenince buton
        // dogru adima ilerler (onceden bu durum bos mesajli 500 olarak donuyordu).
        console.info('[Migros] Siparis Migros tarafinda zaten bu durumda:', { orderId, migrosStatus: result.migrosStatus });
        setSnackbar({
          open: true,
          message: result.message || 'Siparis Migros tarafinda zaten bu durumda. Liste guncellendi.',
          severity: 'info',
        });
        await fetchMigrosOrders();
      } else if (result.statusMismatch) {
        // Migros'ta farkli bir durumda (ornegin baska kullanici onaylamis) - DB esitlendi.
        console.warn('[Migros] Siparis durumu uyusmazligi (self-heal):', { orderId, migrosStatus: result.migrosStatus });
        setSnackbar({
          open: true,
          message: result.message || `Siparis Migros tarafinda farkli durumda. Liste guncellendi.`,
          severity: 'warning',
        });
        await fetchMigrosOrders();
      } else {
        setSnackbar({ open: true, message: result.message || 'Migros siparis durumu guncellenemedi', severity: 'error' });
      }
      return;
    }

    // TRENDYOL YEMEK siparisleri
    if (selectedOrder?.source === 'trendyol') {
      console.log('[StatusUpdate] Trendyol siparisi:', { orderId, newStatus });

      if (newStatus === 'TRD_CANCEL') {
        const trdOrder = trendyolOrders.find(o => {
          const converted = convertTrendyolOrderToPackageOrder(o);
          return converted.id === orderId;
        });
        if (trdOrder) {
          setTrendyolCancelOrder(trdOrder);
          setTrendyolCancelModalOpen(true);
        }
        return;
      }

      const packageId = selectedOrder.trendyolPackageId;
      if (!packageId) {
        setSnackbar({ open: true, message: 'Uber Eats paket ID bulunamadı', severity: 'error' });
        return;
      }

      let action: 'accept' | 'ready' | 'shipped' | 'delivered' = 'accept';
      let statusLabel = '';
      if (newStatus === 'TRD_ACCEPT') { action = 'accept'; statusLabel = 'Onaylandı'; }
      else if (newStatus === 'TRD_READY') { action = 'ready'; statusLabel = 'Hazırlandı'; }
      else if (newStatus === 'TRD_SHIPPED') { action = 'shipped'; statusLabel = 'Yola Çıktı'; }
      else if (newStatus === 'TRD_DELIVERED') { action = 'delivered'; statusLabel = 'Teslim Edildi'; }

      const isGoDelivery = selectedOrder.trendyolDeliveryType === 'GO';

      if (isGoDelivery && action === 'delivered') {
        console.log('[StatusUpdate] Trendyol GO sipariş - sadece SQL güncelleniyor, Trendyol API atlanıyor...', { packageId });
        try {
          await updateStatusInSql(packageId, 'Delivered', undefined, undefined, screenData?.branchId || null);

          setTrendyolOrders(prev => prev.map(o =>
            o.id === packageId ? { ...o, packageStatus: 'Delivered', gtsPaid: true } : o
          ));

          setSnackbar({ open: true, message: 'Uber Eats sipariş: Teslim Edildi', severity: 'success' });

          if (!selectedOrder.isPaid) {
            console.log('[ONLINE_ODEME] Trendyol GO teslim edildi - manuel ödeme bekleniyor (oto kayıt devre dışı)');
          }
        } catch (err) {
          console.error('[StatusUpdate] Trendyol GO SQL güncelleme hatası:', err);
          setSnackbar({ open: true, message: 'Uber Eats sipariş durumu güncellenemedi', severity: 'error' });
        }
        return;
      }

      const result = await updateTrendyolOrderStatus(packageId, action);
      if (result.success) {
        setSnackbar({ open: true, message: `Uber Eats sipariş: ${statusLabel}`, severity: 'success' });

        if (action === 'ready' && selectedOrder?.trendyolDeliveryType === 'STORE') {
          setTimeout(() => { setSelectCourierModalOpen(true); }, 500);
        }

        if (action === 'delivered' && selectedOrder.isOnlinePayment && !selectedOrder.isPaid) {
          console.log('[ONLINE_ODEME] Trendyol teslim edildi - manuel ödeme bekleniyor (oto kayıt devre dışı)');
        }
      } else if (result.statusMismatch) {
        // 409 self-heal: yerel state ve SQL hook tarafından güncellendi, kullanıcıya bilgi ver.
        const mismatchMsg = buildTrendyolMismatchMessage(result.currentStatus, result.attemptedAction);
        console.warn('[Trendyol] Sipariş durumu uyuşmazlığı (self-heal):', { packageId, attempted: action, current: result.currentStatus });
        setSnackbar({ open: true, message: mismatchMsg, severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: result.message || 'Uber Eats sipariş durumu güncellenemedi', severity: 'error' });
      }
      return;
    }

    // YEMEKSEPETI siparisleri
    if (selectedOrder?.source === 'yemeksepeti') {
      console.log('[StatusUpdate] Yemeksepeti siparisi:', { orderId, newStatus });

      if (newStatus === 'YS_ASSIGN_COURIER') {
        setSelectCourierModalOpen(true);
        return;
      }

      let action = '';
      if (newStatus === 'YS_ACCEPT') action = 'accept';
      else if (newStatus === 'YS_PREPARED') action = 'prepared';
      else if (newStatus === 'YS_PICKUP') {
        try {
          const res = (await yemeksepetiApi.post('/api/ys-orders/picked-up', { OrderId: orderId })).data;
          const resSuccess = res.Success ?? res.success;
          const resMessage = res.Message ?? res.message;
          if (resSuccess) {
              const isPickupOrder = (selectedOrder?.yemeksepetiOrderType || '').toUpperCase() === 'PICKUP'
                || (selectedOrder?.yemeksepetiExpeditionType || '').toUpperCase() === 'PICKUP';
              setSnackbar({
                open: true,
                message: isPickupOrder ? 'Musteriye teslim edildi!' : 'Kuryeye teslim edildi!',
                severity: 'success',
              });
              await fetchYemeksepetiOrders();

              // Vendor delivery (restoranın kendi kuryesi) durumunda sipariş burada teslim edilmiş sayılır
              // Online ödemeli ise ödeme kaydını burada da oluştur (accept'te zaten oluşturulmuş olabilir, server dedup yapar)
              const ysRawOrder = yemeksepetiOrders.find(o => o.id === orderId);
              const isVendorDelivery = ysRawOrder && (
                (ysRawOrder.orderType || '').toUpperCase() === 'VENDOR_DELIVERY' ||
                (ysRawOrder.expeditionType || '').toUpperCase() === 'DELIVERY_BY_RESTAURANT'
              );
              if (isVendorDelivery && selectedOrder?.isOnlinePayment && !selectedOrder?.isPaid) {
                console.log('[ONLINE_ODEME] Yemeksepeti vendor kuryeye teslim edildi - manuel ödeme bekleniyor (oto kayıt devre dışı)', { orderId });
              }
            } else {
              setSnackbar({ open: true, message: resMessage || 'Durum guncellenemedi', severity: 'error' });
            }
        } catch (err) {
          console.error('[YS] Kuryeye teslim hatasi:', err);
        }
        return;
      }

      if (action) {
        const result = await updateYemeksepetiOrderStatus(orderId, action);
        if (result.success) {
          const statusLabels: Record<string, string> = { accept: 'Onaylandi', prepared: 'Hazirlandi' };
          setSnackbar({ open: true, message: `Yemeksepeti siparis: ${statusLabels[action] || 'Guncellendi'}`, severity: 'success' });
          await fetchYemeksepetiOrders();

          // Online ödemeli Yemeksepeti siparişleri için ödeme kaydını sipariş onaylanınca oluştur
          // (Diğer platformlarda teslim edilince oluşturuluyor, YS için teslim durumu polling ile güvenilir şekilde yakalanamıyor)
          if (action === 'accept' && selectedOrder?.isOnlinePayment && !selectedOrder?.isPaid) {
            console.log('[ONLINE_ODEME] Yemeksepeti siparis onaylandi - manuel ödeme bekleniyor (oto kayıt devre dışı)', { orderId });
          }

          if (action === 'prepared') {
            const ysOrder = yemeksepetiOrders.find(o => o.id === orderId);
            const isVendorDelivery = ysOrder && (
              (ysOrder.orderType || '').toUpperCase() === 'VENDOR_DELIVERY' ||
              (ysOrder.expeditionType || '').toUpperCase() === 'DELIVERY_BY_RESTAURANT'
            );
            if (isVendorDelivery) {
              setTimeout(() => { setSelectCourierModalOpen(true); }, 500);
            }
          }
        } else {
          setSnackbar({ open: true, message: result.message || 'Yemeksepeti siparis durumu guncellenemedi', severity: 'error' });
        }
      }
      return;
    }
    
    try {
      console.log('[StatusUpdate] Siparis durumu guncelleniyor...', { orderId, newStatus, companyId, courierFirebaseUid, courierName });
      
      // DELIVERED durumu için önce güncel durumu kontrol et
      if (newStatus === 'DELIVERED') {
        console.log('🔍 [StatusUpdate] Teslim öncesi durum kontrolü yapılıyor...');
        
        const currentOrdersResponse = await getTodayPackageOrders(screenData?.branchId || undefined);
        
        if (currentOrdersResponse.Success && currentOrdersResponse.Data) {
          const currentOrder = currentOrdersResponse.Data.find((o: any) => o.Id === orderId);
          
          if (currentOrder) {
            const currentStatus = currentOrder.OrderStatus;
            console.log('🔍 [StatusUpdate] Güncel durum:', currentStatus);
            
            if (currentStatus === 'DELIVERED') {
              setSnackbar({ 
                open: true, 
                message: '⚠️ Bu sipariş zaten teslim edilmiş! Sayfa yenileniyor...', 
                severity: 'warning' 
              });
              await loadPackageOrders();
              return;
            }
            
            if (currentStatus === 'CANCELLED') {
              setSnackbar({ 
                open: true, 
                message: '⚠️ Bu sipariş iptal edilmiş! Sayfa yenileniyor...', 
                severity: 'warning' 
              });
              await loadPackageOrders();
              return;
            }
          }
        }
      }
      
      const response = await updatePackageOrderStatus(
        orderId,
        newStatus,
        currentUser?.email || undefined,
        courierFirebaseUid,
        courierName
      );
      
      if (response.Success) {
        console.log('✅ [StatusUpdate] Sipariş durumu güncellendi');
        
        if (newStatus === 'DELIVERED') {
          try {
            console.log('📡 [StatusUpdate] Kuryeye SignalR bildirimi gönderiliyor...');
            await mainApi.post('/api/CourierActiveOrders/notify-delivery', {
              packageOrderId: orderId
            });
            console.log('✅ [StatusUpdate] SignalR bildirimi gönderildi');
          } catch (signalrError) {
            console.warn('⚠️ [StatusUpdate] SignalR bildirimi gönderilemedi (kritik değil):', signalrError);
          }
        }
        
        await loadPackageOrders();
        
        const statusLabels: { [key: string]: string } = {
          'READY': 'Hazır',
          'ON_DELIVERY': 'Kurye',
          'DELIVERED': 'Teslim Edildi',
          'UNDELIVERED': 'Teslim Edilemedi',
          'CANCELLED': 'İptal',
        };
        
        let successMessage = `✅ Sipariş "${statusLabels[newStatus] || newStatus}" olarak güncellendi`;
        if (courierName && newStatus === 'ON_DELIVERY') {
          successMessage = `✅ Sipariş ${courierName} kuryesine atandı`;
        }
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
      } else {
        const errorMessage = response.Message || 'Bilinmeyen hata';
        
        if (errorMessage.toLowerCase().includes('teslim') || errorMessage.toLowerCase().includes('delivered')) {
          await loadPackageOrders();
        }
        
        setSnackbar({ open: true, message: `Durum güncellenemedi: ${errorMessage}`, severity: 'error' });
      }
    } catch (error) {
      console.error('❌ [StatusUpdate] Hata:', error);
      setSnackbar({ open: true, message: `Durum güncelleme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, severity: 'error' });
    }
  }, [companyId, currentUser?.email, loadPackageOrders, screenData?.branchId, filteredOrders, selectedOrderSource, updateMasaSiparisiStatus, fetchMasaSiparisleri, getirOrders, updateGetirOrderStatus, fetchGetirOrders, handleOnlinePaymentRecord, migrosOrders, updateMigrosOrderStatus, fetchMigrosOrders, trendyolOrders, updateTrendyolOrderStatus, yemeksepetiOrders, updateYemeksepetiOrderStatus, fetchYemeksepetiOrders, setGetirCountdownMap, setSelectCourierModalOpen, setTrendyolCancelOrder, setTrendyolCancelModalOpen, setTrendyolOrders, refreshTrendyolOrders, setSnackbar, onGetirOrderCancelled]);

  const forceConfirmGetirScheduledOrder = useCallback(async (orderId: number) => {
    try {
      console.log(`🔄 [Getir] İleri tarihli sipariş zorla onaylanıyor: ${orderId}`);
      const result = await updateGetirOrderStatus(orderId, 'verify');
      if (result.success) {
        setSnackbar({ open: true, message: 'Getir ileri tarihli sipariş onaylandı', severity: 'success' });
        setGetirCountdownMap(prev => ({ ...prev, [orderId]: 60 }));
        await fetchGetirOrders();
      } else {
        const errorMsg = result.message || 'Bilinmeyen hata';
        if (errorMsg.toLowerCase().includes('iptal') || errorMsg.toLowerCase().includes('cancel')) {
          const getirOrder = getirOrders.find(o => o.id === orderId);
          onGetirOrderCancelled?.({
            getirId: getirOrder?.getirId || String(orderId),
            status: 1500,
            confirmationId: getirOrder?.confirmationId || '',
            cancelReasonText: errorMsg,
            clientName: getirOrder?.customerName || '',
            cancelledAt: new Date().toISOString(),
          });
          await fetchGetirOrders();
        } else {
          setSnackbar({ open: true, message: `Onay başarısız: ${errorMsg}`, severity: 'error' });
        }
      }
    } catch (error) {
      console.error('❌ [Getir] İleri tarihli sipariş onay hatası:', error);
      setSnackbar({ open: true, message: `Onay hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, severity: 'error' });
    }
  }, [updateGetirOrderStatus, fetchGetirOrders, setGetirCountdownMap, setSnackbar, getirOrders, onGetirOrderCancelled]);

  return { handleUpdateOrderStatus, forceConfirmGetirScheduledOrder };
}
